// Mailing-address form shared by the no-login gift page (/gift/:token) and the
// Profile tab. Field rules mirror puttr-api src/giftAddress.js validateAddress;
// the server is the authority and its 422 errors are shown per field.
import React, { useState } from 'react';
import Form from 'react-bootstrap/Form';
import { COUNTRIES, regionsFor, regionLabel, postcodeLabel } from './addressData';

const EMPTY = { shipName: '', address1: '', address2: '', city: '', province: '', zip: '', country: 'US', phone: '' };

function clientValidate(a) {
  const e = {};
  if (!a.shipName.trim()) e.shipName = 'Name is required';
  if (!a.address1.trim()) e.address1 = 'Address is required';
  if (!a.city.trim()) e.city = 'City is required';
  if (!a.country) e.country = 'Country is required';
  if (regionsFor(a.country) && !a.province) e.province = regionLabel(a.country) + ' is required';
  if (!a.zip.trim() && !['IE', 'HK', 'AE', 'QA', 'BS', 'PA'].includes(a.country)) e.zip = postcodeLabel(a.country) + ' is required';
  if (a.country === 'US' && a.zip.trim() && !/^\d{5}(-\d{4})?$/.test(a.zip.trim())) e.zip = 'Enter a 5-digit ZIP code';
  const digits = a.phone.replace(/\D/g, '');
  if (!a.phone.trim()) e.phone = 'Phone is required';
  else if (digits.length < 7) e.phone = 'Enter a phone number with the area code';
  return e;
}

// props: initial (address or null), defaults ({shipName, country}), onSave(address) -> Promise,
// onCancel (optional), submitLabel, cancelLabel, dark (bool: wraps in data-bs-theme="dark")
export default function AddressForm({ initial, defaults, onSave, onCancel, submitLabel = 'Save address', cancelLabel = 'Cancel', dark = false }) {
  const [a, setA] = useState({ ...EMPTY, ...(defaults || {}), ...(initial || {}) });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const set = (k) => (ev) => {
    const v = ev.target.value;
    setA(prev => {
      const next = { ...prev, [k]: v };
      // Switching country changes whether province is a select; clear a value that no longer fits.
      if (k === 'country' && regionsFor(v) && !regionsFor(v).some(r => r[0] === prev.province)) next.province = '';
      return next;
    });
    if (errors[k]) setErrors(prev => ({ ...prev, [k]: undefined }));
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (saving) return; // double-submit guard
    const e = clientValidate(a);
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true); setFormError(null);
    try {
      await onSave({ ...a, shipName: a.shipName.trim(), address1: a.address1.trim(), address2: a.address2.trim(), city: a.city.trim(), zip: a.zip.trim(), phone: a.phone.trim() });
    } catch (err) {
      const data = err && err.response && err.response.data;
      if (data && data.errors) setErrors(data.errors);
      else setFormError('Something went wrong saving your address. Please try again.');
      setSaving(false);
    }
  };

  const regions = regionsFor(a.country);
  const field = (k, label, props = {}) => (
    <Form.Group className="addrField" controlId={'addr_' + k}>
      <Form.Label className="addrLabel">{label}</Form.Label>
      <Form.Control value={a[k]} onChange={set(k)} isInvalid={!!errors[k]} {...props} />
      <Form.Control.Feedback type="invalid">{errors[k]}</Form.Control.Feedback>
    </Form.Group>
  );

  return (
    <Form noValidate onSubmit={submit} className="addrForm" data-bs-theme={dark ? 'dark' : undefined}>
      {field('shipName', 'Full name', { autoComplete: 'name', placeholder: 'Name on the parcel' })}
      <Form.Group className="addrField" controlId="addr_country">
        <Form.Label className="addrLabel">Country</Form.Label>
        <Form.Select value={a.country} onChange={set('country')} isInvalid={!!errors.country} autoComplete="country">
          {COUNTRIES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
        </Form.Select>
        <Form.Control.Feedback type="invalid">{errors.country}</Form.Control.Feedback>
      </Form.Group>
      {field('address1', 'Address line 1', { autoComplete: 'address-line1', placeholder: 'Street and number' })}
      {field('address2', 'Address line 2 (optional)', { autoComplete: 'address-line2', placeholder: 'Apartment, suite, unit' })}
      <div className="addrTwoUp">
        {field('city', a.country === 'GB' ? 'Town / City' : 'City', { autoComplete: 'address-level2' })}
        {regions ? (
          <Form.Group className="addrField" controlId="addr_province">
            <Form.Label className="addrLabel">{regionLabel(a.country)}</Form.Label>
            <Form.Select value={a.province} onChange={set('province')} isInvalid={!!errors.province} autoComplete="address-level1">
              <option value="">Select</option>
              {regions.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
            </Form.Select>
            <Form.Control.Feedback type="invalid">{errors.province}</Form.Control.Feedback>
          </Form.Group>
        ) : field('province', regionLabel(a.country), { autoComplete: 'address-level1' })}
      </div>
      <div className="addrTwoUp">
        {field('zip', postcodeLabel(a.country), { autoComplete: 'postal-code', inputMode: a.country === 'US' ? 'numeric' : 'text' })}
        {field('phone', 'Phone', { autoComplete: 'tel', type: 'tel', inputMode: 'tel' })}
      </div>
      {formError ? <div className="addrFormError">{formError}</div> : null}
      <div className="addrActions">
        <button type="submit" className="btnGreen" disabled={saving}>{saving ? 'Saving…' : submitLabel}</button>
        {onCancel ? <button type="button" className="btnGhost" onClick={onCancel} disabled={saving}>{cancelLabel}</button> : null}
      </div>
    </Form>
  );
}
